import { setLoading } from "../../../redux/actions/app";
import { updateCustomerThunk } from "../../../redux/actions/user";
import { Form, Formik } from "formik";
import KC_UIText from "../../../designsystem/typography/KC_UIText/KC_UIText";
import { KC_Weights } from "../../../designsystem/ds_commons";
import KC_Input from "../../../designsystem/KC_FormInput/KC_Input";
import KC_Button, {
  KC_ButtonSize
} from "../../../designsystem/KC_Button/KC_Button";
import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useAccountToDisplaySelector } from "../../../redux/actions/user";
import { REQUIRED_INFO_SECTOR, TIME_ZONES } from "../../../utils/constants";
import { tt } from "../../../utils/i18n/i18n";
import BusinessUser from "@tootsweet/model/entities/BusinessUser";
import KC_PhoneInput from "../../../designsystem/KC_PhoneInput/KC_PhoneInput";
import { Currencies } from "@tootsweet/model/enums/Currencies";

interface Props {
  customer: BusinessUser | undefined;
}

export default function (props: Props) {
  const dispatch = useDispatch();
  const { accountToDisplay } = useAccountToDisplaySelector();

  const currency = useMemo(() => {
    return accountToDisplay?.dCurrency || accountToDisplay?.currency;
  }, [accountToDisplay]);

  const handleSubmit = (values: BusinessUser) => {
    try {
      dispatch(setLoading(true));
      dispatch(
        updateCustomerThunk(
          {
            contactName: values.contactName,
            contactPhoneNumber: values.contactPhoneNumber,
            avgBasket: values.avgBasket,
            numberOfTables: values.numberOfTables,
            tz: values.tz,
            dCurrency: values.dCurrency
          },
          props.customer?.trackingID,
          accountToDisplay?.token
        )
      );
    } catch (err) {
      console.error(err);
      alert(tt("error"));
    } finally {
      setTimeout(() => {
        dispatch(setLoading(false));
      }, 500);
    }
  };

  const displayNumberOfTables = useMemo(() => {
    return REQUIRED_INFO_SECTOR.includes(props.customer?.customerType || "");
  }, [props.customer]);

  if (!props.customer) {
    return null;
  }

  return (
    <Formik<BusinessUser>
      key={props.customer.trackingID}
      initialValues={props.customer}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ values, setFieldValue, dirty }) => {
        return (
          <Form id="operational-info-form" className="h-100" autoComplete="off">
            <div className="w-100 d-flex flex-column st-gap h-100">
              <div>
                <label htmlFor="contactName">
                  <KC_UIText
                    weight={KC_Weights.md}
                    text={tt("jp_onboarding_form_label_operational_contact")}
                  />
                </label>
                <KC_Input
                  id="contactName"
                  htmlFor="contactName"
                  placeholder={""}
                  type="text"
                  setFieldValue={setFieldValue}
                  required={true}
                  value={values?.contactName}
                  disabled={false}
                />
              </div>

              <div>
                <label htmlFor="contactPhoneNumber">
                  <KC_UIText
                    weight={KC_Weights.md}
                    text={tt(
                      "jp_onboarding_form_label_operational_contact_phone"
                    )}
                  />
                </label>
                <KC_PhoneInput
                  id="contactPhoneNumber"
                  htmlFor="contactPhoneNumber"
                  placeholder={""}
                  setFieldValue={setFieldValue}
                  required={true}
                  value={values?.contactPhoneNumber || ""}
                  defaultCountry={
                    (values?.shippingAddress?.countryCode?.toLowerCase() as any) ||
                    "fr"
                  }
                />
              </div>

              <div>
                <label htmlFor="dCurrency">
                  <KC_UIText
                    weight={KC_Weights.md}
                    text={tt("display_currency")}
                  />
                </label>
                <div className="kc-input-wrapper">
                  <select
                    id="dCurrency"
                    value={values.dCurrency || values.currency}
                    className="kc_input"
                    onChange={(e) => {
                      void setFieldValue("dCurrency", e.target.value);
                    }}
                  >
                    {Object.values(Currencies).map((curr) => (
                      <option key={curr} value={curr}>
                        {curr}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="avgBasket">
                  <KC_UIText
                    weight={KC_Weights.md}
                    text={tt("averageBasketLabel")}
                  />
                </label>
                <KC_Input
                  id="avgBasket"
                  htmlFor="avgBasket"
                  placeholder={""}
                  type="number"
                  min={1}
                  step={1}
                  setFieldValue={setFieldValue}
                  required={true}
                  value={values?.avgBasket}
                  disabled={false}
                  appendElementText={values.dCurrency || currency}
                />
              </div>

              {displayNumberOfTables && (
                <div>
                  <label htmlFor="numberOfTables">
                    <KC_UIText
                      weight={KC_Weights.md}
                      text={tt("numberOfTablesLabel")}
                    />
                  </label>
                  <KC_Input
                    id="numberOfTables"
                    htmlFor="numberOfTables"
                    placeholder={""}
                    type="number"
                    min={1}
                    step={1}
                    setFieldValue={setFieldValue}
                    required={true}
                    value={values?.numberOfTables}
                    disabled={false}
                  />
                </div>
              )}

              <div>
                <label htmlFor="tz">
                  <KC_UIText weight={KC_Weights.md} text={tt("time_zone")} />
                </label>
                <div className="kc-input-wrapper">
                  <select
                    value={values.tz}
                    className="kc_input"
                    onChange={(e) => {
                      void setFieldValue("tz", e.target.value);
                    }}
                  >
                    {TIME_ZONES.map((zone) => (
                      <option key={zone} value={zone}>
                        {zone}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex-grow-1" />

              <KC_Button
                id="save"
                type="submit"
                text={tt("save")}
                disabled={!dirty}
                size={KC_ButtonSize.Large}
                className="d-flex"
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}
